import { User } from '@auth0/auth0-react'
import { DialogContent } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { addBusinessDays } from 'date-fns'
import React, { useCallback } from 'react'
import { IoMdAlert } from 'react-icons/io'
import { IoDocumentTextOutline } from 'react-icons/io5'
import { useTheme } from 'styled-components'

import { Dispensary } from '../../../../graphql/camel'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { removeDispensaryPrefixAndSlug } from '../../../../lib/dispensaries'
import { setPractitionerFormsState } from '../../../../lib/onboarding/practitioner-forms-state'
import { usePageUrl } from '../../../../lib/pages'
import { useLocationContext } from '../../../../lib/pages/hooks/useLocationContext'
import type { PractitionerSettings } from '../../../../lib/practitioner-settings/types'
import { publishEvent } from './CommissionsNotification'
import {
  FormName,
  FormTag,
  HeaderRow,
  InstructionRow,
  MissingFormRow,
  Modal,
  ModalCloseIcon,
  ModalHeader,
  ModalSubheader,
  ModalTitle,
  OptOutButton,
  RemindButton,
  SubmitDocumentsButton,
} from './styled'

const useIsExcludedPath = (excludedPathPatterns?: RegExp[]): boolean => {
  const location = useLocationContext()
  const path = removeDispensaryPrefixAndSlug(location?.pathname ?? '/')
  const patterns = [...(excludedPathPatterns ?? [])]
  return patterns.some((pattern) => pattern.test(path))
}

interface CommissionsModalProps {
  practitionerFormsAreNeeded: boolean
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  reminderIsOverdue: boolean
  practitionerSettings?: PractitionerSettings['practitioner']
  excludedPathPatterns: RegExp[]
  notificationType: string
  className?: string
  user?: User
  dispensary?: Dispensary
}

const CommissionsModal: React.FC<CommissionsModalProps> = ({
  practitionerFormsAreNeeded,
  showModal,
  setShowModal,
  reminderIsOverdue,
  practitionerSettings,
  excludedPathPatterns,
  notificationType,
  className,
  user,
  dispensary,
}) => {
  const theme = useTheme()
  const isExcludedPath = useIsExcludedPath(excludedPathPatterns)
  const { eftForm, taxIdForm } = useSiteSettings()
  const { achFormReceived, taxIdFormReceived } = practitionerSettings ?? {}
  const { navigate } = usePageUrl()
  const { commissionsNotificationDaysToHide } = useSiteMetadata() ?? {}
  const daysToHide = commissionsNotificationDaysToHide ?? 10

  const dismissModal = useCallback(() => {
    setShowModal(false)
    publishEvent({
      eventKey: 'commissionsNotificationDismissed',
      user,
      dispensary,
    })

    setPractitionerFormsState({
      lastEvent: 'toast_dismissed',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [setShowModal, user, dispensary, daysToHide])

  const navigateToRewardsOptOut = useCallback(() => {
    navigate('/account/settings/rewards-program')
    publishEvent({
      eventKey: 'commissionsNotificationNavigatedToOptOut',
      user,
      dispensary,
    })
    setPractitionerFormsState({
      lastEvent: 'toast_selected',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [navigate, user, dispensary, daysToHide])

  const navigateToPractitionerForms = useCallback(() => {
    navigate('/account/settings/documents')
    publishEvent({
      eventKey: 'commissionsNotificationNavigatedToSubmit',
      user,
      dispensary,
    })
    setPractitionerFormsState({
      lastEvent: 'toast_selected',
      notifyAfter: addBusinessDays(new Date(), daysToHide).toISOString(),
    })
  }, [navigate, user, dispensary, daysToHide])

  return (
    <Modal
      open={
        practitionerFormsAreNeeded &&
        (notificationType === 'modal' || showModal) &&
        (!!eftForm || !!taxIdForm) &&
        !isExcludedPath &&
        reminderIsOverdue
      }
      onClose={dismissModal}
      className={className}
      aria-describedby="scroll-dialog-description"
      aria-labelledby="scroll-dialog-title"
    >
      <DialogContent>
        <ModalTitle disableTypography id="scroll-dialog-title">
          <HeaderRow>
            <IoMdAlert color={theme.colors.semantic.error} size={18} />
            <ModalHeader>Important Actions Pending</ModalHeader>
            <ModalCloseIcon aria-label="close" onClick={dismissModal}>
              <CloseIcon fontSize="small" />
            </ModalCloseIcon>
          </HeaderRow>

          <ModalSubheader>Your dispensary is not currently generating commissions</ModalSubheader>
          <InstructionRow>
            Please submit the required documents to start earning commissions.
          </InstructionRow>
        </ModalTitle>

        {!achFormReceived && !!eftForm && (
          <MissingFormRow>
            {' '}
            <IoDocumentTextOutline size={24} /> <FormName>{eftForm.label || 'EFT'} Form</FormName>{' '}
            <FormTag>Missing</FormTag>{' '}
          </MissingFormRow>
        )}
        {!taxIdFormReceived && !!taxIdForm && (
          <MissingFormRow>
            <IoDocumentTextOutline size={24} />{' '}
            <FormName>{taxIdForm.label} Taxpayer Identification Form</FormName>{' '}
            <FormTag>Missing</FormTag>{' '}
          </MissingFormRow>
        )}

        <SubmitDocumentsButton onClick={navigateToPractitionerForms}>
          Continue to submit documents
        </SubmitDocumentsButton>
        <RemindButton onClick={dismissModal}>Remind me later</RemindButton>
        <OptOutButton onClick={navigateToRewardsOptOut}>Opt out of commissions</OptOutButton>
      </DialogContent>
    </Modal>
  )
}

export default CommissionsModal
